// @greenlight-spec: 12-azure-installation.md
// @greenlight-spec: 36-edge-routing-dns-tls.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 39-gcp-installation.md
// @greenlight-spec: decisions/0016-single-delegated-zone-dns-tls.md

import * as React from 'react';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { PipelineCheckRow } from '@/components/greenlight/PipelineCheckRow';
import { StepHeader } from '@/components/install/StepHeader';
import {
  CHECK_LABELS,
  CHECK_ORDER,
  PRIVATE_CHECK_LABELS,
  announce,
  checkStatus,
  isRealFailure,
  latchDnsSuccess,
} from '@/components/install/steps/dnsHttpsChecks';
import { api } from '@/lib/api';
import { errorBodyMessage } from '@/lib/apiErrors';
import { DOCS_LINKS } from '@/lib/docsLinks';
import { revealElement } from '@/lib/reveal';
import type { ControlPlaneHttpsVerify, InstallCloud, InstallDnsSetup } from '@greenlight/contract';

/** Flat interval, measured from the previous check's completion — a one-time
 * install has no need for backoff, and overlapping checks would double-count. */
const POLL_INTERVAL_MS = 10_000;

// The cloud's managed-DNS API name, for the egress-blocked remediation copy (docs/39).
const DNS_API_NAME: Record<InstallCloud['provider'], string> = {
  azure: 'Azure DNS API',
  gcp: 'Cloud DNS API',
  aws: 'Route 53 API',
  localfake: 'DNS API',
};

interface DnsHttpsStepProps {
  /** The delegated zone / final control-plane host. */
  appsDomain: string;
  /** Which cloud's DNS API to name in the egress-blocked remediation copy. */
  cloud: InstallCloud['provider'];
  /** The verified final HTTPS URL to hand off to, e.g. `https://greenlight.acme.com/install`. */
  finalUrl: string;
  /** True when the wizard is already loaded on the final HTTPS origin. */
  onFinalOrigin: boolean;
  /**
   * The host the wizard is loaded from. Before private DNS exists, a private
   * install's wizard is opened at the address that reaches Greenlight — the
   * internal address when peered, the endpoint's address through private link —
   * so that address is what the private records point at.
   */
  pageHost?: string;
  /**
   * True when this screen is the wizard's next required step. Everything
   * automatic — the record upsert, the verification poll, the handoff — runs
   * only then, so visiting the screen as a preview changes nothing.
   */
  active: boolean;
  /** Persisted `dns.details.records_upserted` (or a complete `dns` step). */
  recordsUpserted: boolean;
  /** Persisted `control_plane_https` completion. */
  httpsVerified: boolean;
  /** Persisted `verified_url_handoff` completion. */
  handoffComplete: boolean;
  /** Re-read install status once the server marks dns + control_plane_https complete. */
  onVerified: () => Promise<void>;
  /** Mark `verified_url_handoff` complete — fires automatically on the final
   * origin. Resolves false when the server refused, so the screen can recover. */
  onHandoff: () => Promise<boolean>;
}

const IPV4_ADDRESS = /^\d{1,3}(\.\d{1,3}){3}$/;

/**
 * DNS delegation and certificate issuance as one self-driving screen: Greenlight
 * writes the platform A records itself and polls the readiness checks. The only
 * manual action is the NS delegation in the parent zone. Once HTTPS is live,
 * Continue advances the wizard and opens the Greenlight host over HTTPS.
 *
 * The three server steps (`dns`, `control_plane_https`, `verified_url_handoff`)
 * keep their own persisted state and gating; this screen just stops presenting
 * them as three destinations.
 */
export function DnsHttpsStep({
  appsDomain,
  cloud,
  finalUrl,
  onFinalOrigin,
  pageHost = '',
  active,
  recordsUpserted,
  httpsVerified,
  handoffComplete,
  onVerified,
  onHandoff,
}: DnsHttpsStepProps) {
  const setupQuery = api.install.getDnsSetup.useQuery(['install-dns-setup'], {});
  const upsertMutation = api.install.upsertPlatformDnsRecords.useMutation();
  const verifyMutation = api.install.verifyControlPlaneHttps.useMutation();

  const [upsertedSetup, setUpsertedSetup] = React.useState<InstallDnsSetup | null>(null);
  const [upsertError, setUpsertError] = React.useState<string | null>(null);
  // In-flight flags are owned here rather than read off the mutations: a request
  // started before a remount settles against the old observer, which would leave
  // `isPending` stuck true and the screen claiming work that already finished.
  const [upserting, setUpserting] = React.useState(false);
  const [upsertSettled, setUpsertSettled] = React.useState(false);
  const [result, setResult] = React.useState<ControlPlaneHttpsVerify | null>(null);
  const [checking, setChecking] = React.useState(false);
  const [verifyError, setVerifyError] = React.useState<string | null>(null);
  const [startedAt, setStartedAt] = React.useState<number | null>(null);
  const [nextCheckAt, setNextCheckAt] = React.useState<number | null>(null);
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  const [tabHidden, setTabHidden] = React.useState(() => document.hidden);
  const [handoffFailed, setHandoffFailed] = React.useState(false);

  const setup = upsertedSetup ?? (setupQuery.data?.status === 200 ? setupQuery.data.body : null);
  const privateInstall = setup?.access_profile === 'private';
  const privateRecordTarget = IPV4_ADDRESS.test(pageHost)
    ? pageHost
    : (setup?.ingress_target ?? '');
  const setupFailed =
    setupQuery.isError || (setupQuery.data !== undefined && setupQuery.data.status !== 200);
  const allPassed = httpsVerified || result?.ok === true;

  // ── Automatic platform-record upsert ──────────────────────────────────────
  const runUpsert = async (): Promise<void> => {
    setUpsertError(null);
    setUpserting(true);
    try {
      const res = await upsertMutation.mutateAsync({ body: {} });
      if (res.status === 200) setUpsertedSetup(res.body);
      else setUpsertError(errorBodyMessage(res.body, 'Could not write the DNS records.'));
    } catch {
      setUpsertError('Could not reach the control plane. Check it is running and try again.');
    } finally {
      setUpserting(false);
      setUpsertSettled(true);
    }
  };
  const runUpsertRef = useLatest(runUpsert);

  const upsertAttempted = React.useRef(false);
  React.useEffect(() => {
    if (!active || recordsUpserted || upsertAttempted.current) return;
    upsertAttempted.current = true;
    void runUpsertRef.current();
  }, [active, recordsUpserted, runUpsertRef]);

  // ── Verification poll ─────────────────────────────────────────────────────
  const inFlight = React.useRef(false);
  const runCheck = async (): Promise<void> => {
    if (inFlight.current) return;
    inFlight.current = true;
    setChecking(true);
    setVerifyError(null);
    setStartedAt((previous) => previous ?? Date.now());
    try {
      const res = await verifyMutation.mutateAsync({ body: {} });
      if (res.status === 200) {
        setResult((previous) => latchDnsSuccess(previous, res.body));
        if (res.body.ok) await onVerified();
      } else {
        setVerifyError(errorBodyMessage(res.body, 'Could not run the readiness checks.'));
      }
    } catch {
      setVerifyError('Could not reach the control plane. Check it is running and try again.');
    } finally {
      inFlight.current = false;
      setChecking(false);
      setNextCheckAt(Date.now() + POLL_INTERVAL_MS);
    }
  };
  const runCheckRef = useLatest(runCheck);

  React.useEffect(() => {
    const onVisibility = (): void => setTabHidden(document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Checking before the A records exist would fail the delegation check for a
  // reason Greenlight is about to fix itself, so the first round waits for the
  // upsert to settle (immediately, when a previous run already wrote them).
  const readyToCheck = recordsUpserted || upsertSettled;

  React.useEffect(() => {
    if (!active || allPassed) return;
    // Every later check is scheduled from the previous one's completion.
    if (nextCheckAt === null) {
      if (readyToCheck) void runCheckRef.current();
      return;
    }
    if (tabHidden) return;
    const id = window.setTimeout(
      () => void runCheckRef.current(),
      Math.max(0, nextCheckAt - Date.now()),
    );
    return () => window.clearTimeout(id);
  }, [active, allPassed, nextCheckAt, readyToCheck, tabHidden, runCheckRef]);

  // Drives the countdown and the elapsed-time readout.
  React.useEffect(() => {
    if (!active || allPassed) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1_000);
    return () => window.clearInterval(id);
  }, [active, allPassed]);

  // ── Handoff onto the secure URL ───────────────────────────────────────────
  const runHandoff = async (): Promise<void> => {
    setHandoffFailed(false);
    try {
      if (!(await onHandoff())) {
        setHandoffFailed(true);
        return;
      }
      if (!onFinalOrigin) window.location.href = finalUrl;
    } catch {
      setHandoffFailed(true);
    }
  };
  const runHandoffRef = useLatest(runHandoff);

  // Auto-advance fires once; a refusal surfaces a retry rather than leaving the
  // screen spinning on a step that will never complete by itself.
  const handoffAttempted = React.useRef(false);
  React.useEffect(() => {
    if (!active || !onFinalOrigin || !httpsVerified || handoffComplete) return;
    if (handoffAttempted.current) return;
    handoffAttempted.current = true;
    void runHandoffRef.current();
  }, [active, onFinalOrigin, httpsVerified, handoffComplete, runHandoffRef]);

  const checks = CHECK_ORDER.map((key) => {
    const check = result?.checks[key];
    return { key, status: checkStatus(check, checking, httpsVerified), detail: check?.detail };
  });

  const failures = result ? CHECK_ORDER.map((k) => result.checks[k]).filter(isRealFailure) : [];
  const egressBlocked = failures.some((c) => c.category === 'blocked');
  const waiting = active && !allPassed && failures.length === 0 && result !== null;

  // The 1s tick can lag the schedule by up to a second, so clamp the readout to
  // the interval — "next check in 11s" on a 10s poll reads as a bug.
  const secondsToNextCheck =
    nextCheckAt === null
      ? null
      : Math.min(POLL_INTERVAL_MS / 1000, Math.max(0, Math.ceil((nextCheckAt - nowMs) / 1000)));

  // ── Announce transitions + reveal newly surfaced content ──────────────────
  const announcement = announce(
    result,
    allPassed,
    privateInstall ? PRIVATE_CHECK_LABELS : CHECK_LABELS,
  );
  const revealRef = React.useRef<HTMLDivElement>(null);
  const announced = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (announcement === '') return;
    // The first result lands as the installer is still reading the screen from
    // the top, so it is not "newly revealed" — only later transitions are.
    const isFirst = announced.current === null;
    announced.current = announcement;
    if (!isFirst) revealElement(revealRef.current);
  }, [announcement]);

  return (
    <div className="space-y-6" data-testid="step-dns_https">
      <StepHeader step="dns" title="DNS & HTTPS">
        {privateInstall ? (
          <span data-testid="private-dns-intro">
            A private install takes two DNS actions from your team: delegate one public zone so
            Greenlight can prove the certificate, and publish two names in your private DNS so your
            network reaches Greenlight. Greenlight then watches until the certificate is ready.
          </span>
        ) : (
          'Greenlight uses one delegated DNS zone. You make a single delegation in your parent DNS; Greenlight creates every record inside the zone, then watches until HTTPS is live.'
        )}
      </StepHeader>

      <p aria-live="polite" className="sr-only" data-testid="dns-https-announcement">
        {announcement}
      </p>

      {setupFailed && (
        <Alert variant="destructive" data-testid="dns-error">
          <AlertDescription>
            Could not load the DNS setup. Check the control plane is running and try refreshing.
          </AlertDescription>
        </Alert>
      )}

      {setupQuery.isLoading && (
        <div className="space-y-3" data-testid="dns-loading">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      )}

      {setup && (
        <>
          <section className="space-y-3" data-testid="dns-ns-records">
            <div>
              <h2 className="text-base font-semibold tracking-tight">
                {privateInstall ? '1. Create these NS records' : 'Create these NS records'}
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                In <MonoValue wrap>{setup.apps_domain}</MonoValue>&apos;s parent zone, delegate{' '}
                <MonoValue wrap>{setup.apps_domain}</MonoValue> to the nameservers below.
                {privateInstall && (
                  <>
                    {' '}
                    <span data-testid="private-dns-delegation-purpose">
                      The public zone holds only the certificate challenge records Greenlight
                      writes; it never gets an address for Greenlight or your apps.
                    </span>
                  </>
                )}
              </p>
            </div>
            <ul className="grid w-fit grid-cols-[auto_auto_auto] items-center gap-x-2 gap-y-2 text-sm">
              {setup.ns_records.map((record) => (
                <li
                  key={record.value}
                  className="col-span-3 grid grid-cols-subgrid items-center"
                  data-testid="dns-nameserver"
                >
                  <MonoValue wrap>{record.name}</MonoValue>
                  <span className="text-muted-foreground">NS</span>
                  <MonoValue copyable wrap>
                    {record.value}
                  </MonoValue>
                </li>
              ))}
            </ul>
          </section>

          {privateInstall ? (
            <section className="space-y-3" data-testid="private-dns-records">
              <div>
                <h2 className="text-base font-semibold tracking-tight">
                  2. Publish these names in your private DNS
                </h2>
                <p
                  className="text-sm text-muted-foreground mt-0.5"
                  data-testid="private-dns-publish"
                >
                  {privateRecordTarget === pageHost
                    ? 'Create each record below in the private DNS your company uses, pointing at the address you opened this page at. Do not give either name a public address.'
                    : "Create each record below in the private DNS your company uses. If you connect through a private endpoint, use that endpoint's address instead of the one shown. Do not give either name a public address."}
                </p>
              </div>
              <ul className="grid w-fit grid-cols-[auto_auto_auto] items-center gap-x-2 gap-y-2 text-sm">
                {[setup.control_plane_host || setup.apps_domain, `*.${setup.apps_domain}`].map(
                  (name) => (
                    <li
                      key={name}
                      className="col-span-3 grid grid-cols-subgrid items-center"
                      data-testid="private-dns-record"
                    >
                      <MonoValue wrap>{name}</MonoValue>
                      <span className="text-muted-foreground">A</span>
                      <MonoValue copyable wrap>
                        {privateRecordTarget}
                      </MonoValue>
                    </li>
                  ),
                )}
              </ul>
              <p className="text-sm text-muted-foreground">
                The checks below do not include these names.
              </p>
            </section>
          ) : null}
          {upsertError && (
            <Alert variant="destructive" data-testid="dns-upsert-error">
              <AlertTitle>
                {privateInstall
                  ? 'Could not remove the public DNS records'
                  : 'Could not write the platform DNS records'}
              </AlertTitle>
              <AlertDescription className="space-y-3">
                <p>{upsertError}</p>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full md:w-auto h-11 md:h-9"
                  onClick={() => void runUpsert()}
                  disabled={upserting}
                  data-testid="dns-upsert-retry"
                >
                  {upserting ? 'Retrying…' : 'Retry'}
                </Button>
              </AlertDescription>
            </Alert>
          )}
        </>
      )}

      {!active && !handoffComplete && (
        <div
          className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground"
          data-testid="step-preview"
        >
          You&apos;re previewing this step. Greenlight writes the platform records and starts
          checking automatically once the wizard reaches it.
        </div>
      )}

      {(active || httpsVerified) && (
        <section className="space-y-3">
          <h2 className="text-base font-semibold tracking-tight">Readiness</h2>
          <div className="rounded-lg border" data-testid="https-checks">
            {checks.map((c) => (
              <PipelineCheckRow
                key={c.key}
                name={(privateInstall ? PRIVATE_CHECK_LABELS : CHECK_LABELS)[c.key]}
                status={c.status}
                detail={c.detail}
              />
            ))}
          </div>

          {/* Nothing to count down to until the first check has started; the
              records section above explains the pause while they are written. */}
          {active && !allPassed && startedAt !== null && (
            <div
              className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
              data-testid="https-poll-status"
            >
              <p className="text-sm text-muted-foreground">
                <span data-testid="https-next-check">
                  {checking ? 'Checking…' : `Next check in ${secondsToNextCheck ?? 0}s`}
                </span>
                <span data-testid="https-elapsed">
                  {' · '}
                  {formatElapsed(nowMs - startedAt)} elapsed
                </span>
              </p>
              <Button
                type="button"
                variant="outline"
                className="w-full sm:w-auto h-11 md:h-9"
                onClick={() => void runCheck()}
                disabled={checking}
                data-testid="https-verify-now"
              >
                Verify now
              </Button>
            </div>
          )}
        </section>
      )}

      <div ref={revealRef} className="space-y-6">
        {verifyError && (
          <Alert variant="destructive" data-testid="https-network-error">
            <AlertDescription>{verifyError}</AlertDescription>
          </Alert>
        )}

        {waiting && (
          <Alert data-testid="https-waiting">
            <AlertTitle>Waiting for DNS and the certificate</AlertTitle>
            <AlertDescription>
              Greenlight keeps checking on its own. NS delegation usually propagates within minutes,
              and the wildcard certificate typically issues a few minutes after delegation is
              confirmed. You can leave this page open.
            </AlertDescription>
          </Alert>
        )}

        {active && failures.length > 0 && (
          <Alert data-testid="https-remediation">
            <AlertTitle>Verification needs your attention</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>
                Create NS records for <MonoValue wrap>{appsDomain}</MonoValue> at your parent DNS,
                pointing at the nameservers shown above. Greenlight re-checks every{' '}
                {POLL_INTERVAL_MS / 1000} seconds, so there is nothing to click once they are in
                place.
              </p>
              {egressBlocked && (
                <p data-testid="https-egress-blocked">
                  Outbound DNS, Let&apos;s Encrypt, or {DNS_API_NAME[cloud]} egress appears blocked
                  — confirm your VPC egress policy allows them. This is distinct from delegation not
                  being done.
                </p>
              )}
              <p>
                <a
                  href={DOCS_LINKS.dns}
                  target="_blank"
                  rel="noreferrer"
                  className="text-link underline"
                  data-testid="dns-docs-link"
                >
                  DNS &amp; TLS setup guide
                </a>
              </p>
            </AlertDescription>
          </Alert>
        )}

        {allPassed && !handoffComplete && !onFinalOrigin && privateInstall && setup && (
          <div className="space-y-2" data-testid="private-dns-handoff">
            <p className="text-sm text-muted-foreground" data-testid="private-handoff-next">
              Once both names resolve on your company network, open{' '}
              <MonoValue wrap>{finalUrl}</MonoValue> from there. The remaining steps run on that
              address.
            </p>
          </div>
        )}

        {allPassed && !onFinalOrigin && setup && !privateInstall && (
          <div className="space-y-3" data-testid="handoff-on-bootstrap">
            {handoffFailed ? (
              <Alert variant="destructive" data-testid="handoff-error">
                <AlertTitle>Could not continue</AlertTitle>
                <AlertDescription className="space-y-3">
                  <p>
                    HTTPS is ready, but Greenlight could not finish this step here, and this
                    temporary address may already have stopped working. Open{' '}
                    <MonoValue wrap>{finalUrl}</MonoValue> to continue — the step finishes there
                    automatically.
                  </p>
                  <div className="flex flex-col gap-2 md:flex-row">
                    <Button
                      asChild
                      className="w-full md:w-auto h-11 md:h-9"
                      data-testid="handoff-open-final"
                    >
                      <a href={finalUrl}>Open the HTTPS URL</a>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full md:w-auto h-11 md:h-9"
                      onClick={() => void runHandoff()}
                      data-testid="handoff-retry"
                    >
                      Try again here
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  HTTPS is ready. Continue to your identity provider. The remaining steps run on the
                  Greenlight host, and this temporary HTTP address stops working once you continue.
                </p>
                <Button
                  type="button"
                  className="w-full md:w-auto h-11 md:h-9"
                  onClick={() => void runHandoff()}
                  data-testid="handoff-continue"
                >
                  Continue
                </Button>
              </>
            )}
          </div>
        )}

        {allPassed &&
          !handoffComplete &&
          onFinalOrigin &&
          (handoffFailed ? (
            <Alert variant="destructive" data-testid="handoff-error">
              <AlertTitle>Could not continue on this URL</AlertTitle>
              <AlertDescription className="space-y-3">
                <p>
                  You&apos;re on the verified Greenlight host, but Greenlight could not save this
                  step. Try again — the identity-provider step stays gated until it succeeds.
                </p>
                <Button
                  type="button"
                  className="w-full md:w-auto h-11 md:h-9"
                  onClick={() => void runHandoff()}
                  data-testid="handoff-retry"
                >
                  Try again
                </Button>
              </AlertDescription>
            </Alert>
          ) : (
            <p
              className="flex items-center gap-2 text-sm text-muted-foreground"
              data-testid="handoff-on-final"
            >
              <Loader2 className="size-4 shrink-0 motion-safe:animate-spin" aria-hidden />
              You&apos;re on the verified Greenlight host — continuing to your identity provider.
            </p>
          ))}

        {allPassed && handoffComplete && onFinalOrigin && (
          <p className="text-sm text-muted-foreground" data-testid="dns-https-complete">
            DNS and HTTPS are ready.
          </p>
        )}
      </div>
    </div>
  );
}

/** Keeps a mutable ref pointed at the newest closure, so timer/effect callbacks
 * never re-subscribe just because a handler was re-created this render. */
function useLatest<T>(value: T): React.MutableRefObject<T> {
  const ref = React.useRef(value);
  React.useEffect(() => {
    ref.current = value;
  });
  return ref;
}

function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return minutes === 0 ? `${seconds}s` : `${minutes}m ${seconds}s`;
}
